/**
 * What a comped subscription is, in one place.
 *
 * An "offer" — how generous, on which plans, for how long, to how many people —
 * is described here, and here only. Three callers need that description and
 * they run in three different runtimes: the CLI in `scripts/`, the
 * `admin-create-promo-codes` Edge Function in Deno, and the admin page in the
 * browser, which shows an operator the sentence their choices amount to before
 * they mint anything.
 *
 * Two of those callers mint real money. If they disagreed about what
 * `duration: 'once'` means, the disagreement would be silent: the codes look
 * identical either way and the mistake only surfaces on somebody's invoice
 * months later. So the shaping of the Stripe request lives below all three.
 *
 * ## Why this file imports nothing
 *
 * Deno resolves a relative `.ts` import and nothing else — no workspace
 * aliases, no bare specifiers. Keeping this module dependency-free is what lets
 * the Edge Function reach it with `../../../packages/promo/src/index.ts`
 * instead of growing its own copy, which is the outcome this file exists to
 * prevent.
 *
 * ## Why this is not a way to be entitled
 *
 * Nothing here grants anything. A code is a Stripe coupon; redeeming one makes
 * a real subscription at $0 and the signed webhook writes the entitlement
 * exactly as it does for a paying customer. `subscriptions` has no insert
 * policy and no writer but that webhook (docs/payments.md) — a home-grown
 * "free account" table would be precisely the write path that design forbids.
 */

/**
 * Unambiguous alphabet: no O/0, no I/1. Codes get read off a screen, typed on
 * a phone, and dictated down the phone to a parent.
 */
export const ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

/** Plans a code may be scoped to. `school` is quoted, not sold self-serve. */
export type Tier = 'premium' | 'family';

/** `forever` never charges; `once` covers one billing period; a number is months. */
export type Duration = { kind: 'forever' } | { kind: 'once' } | { kind: 'repeating'; months: number };

/** One batch of codes, fully described. */
export type Offer = {
  count: number;
  percent: number;
  duration: Duration;
  /** Empty means the code works on every plan, monthly or annual. */
  tiers: Tier[];
  maxRedemptions: number;
  /** Unix seconds, or null for no expiry. */
  expiresAt: number | null;
  prefix: string;
  note: string;
  firstTimeOnly: boolean;
};

/** 100% off any plan, for as long as the subscription lasts, one redemption. */
export const DEFAULT_OFFER: Offer = {
  count: 1,
  percent: 100,
  duration: { kind: 'forever' },
  tiers: [],
  maxRedemptions: 1,
  expiresAt: null,
  prefix: 'YUANMATH',
  note: '',
  firstTimeOnly: false,
};

/**
 * How many codes one request may mint.
 *
 * Far below the CLI's 500 on purpose. The CLI is run by somebody holding the
 * Stripe secret key, who could mint anything by hand anyway; the Edge Function
 * is reachable with a *session*, and a stolen admin session should not be able
 * to give away the product in a single call. The cap is the blast radius.
 */
export const MAX_CODES_PER_REQUEST = 50;

/** How long a note may be. Stripe caps metadata values at 500 characters. */
export const MAX_NOTE_LENGTH = 200;

/* -------------------------------------------------------------------------- */
/* Codes                                                                      */
/* -------------------------------------------------------------------------- */

/** Picks an integer in `[0, bound)`. Injected so a code batch can be replayed. */
export type Pick = (bound: number) => number;

/**
 * A cryptographically random `pick`, on Node, Deno and the browser alike.
 *
 * Rejection sampling rather than `% bound`: for a bound that does not divide
 * 256 the modulo quietly favours the first few letters of the alphabet, which
 * is not a real attack at 10^12 possibilities but is the kind of thing that is
 * embarrassing to explain and free to avoid.
 */
export const securePick: Pick = (bound: number): number => {
  if (bound <= 0) throw new RangeError('pick needs a positive bound');
  const limit = Math.floor(256 / bound) * bound;
  const byte = new Uint8Array(1);
  for (;;) {
    globalThis.crypto.getRandomValues(byte);
    const value = byte[0] as number;
    if (value < limit) return value % bound;
  }
};

/**
 * A code like `YUANMATH-K7QF-3XRB`.
 *
 * Two groups of four from a 32-character alphabet is ~10^12 possibilities, so
 * guessing one is not an attack even at `maxRedemptions` above 1. The random
 * source is injected because a generator that cannot be replayed cannot be
 * tested — the same reason every generator below `apps/web` takes an `Rng`.
 */
export function randomCode(prefix: string, pick: Pick = securePick): string {
  const group = (): string =>
    Array.from({ length: 4 }, () => ALPHABET[pick(ALPHABET.length)] ?? 'X').join('');
  const body = `${group()}-${group()}`;
  return prefix ? `${prefix}-${body}` : body;
}

/** Stripe accepts A–Z, 0–9 and hyphens; anything else is rejected on create. */
export function normalisePrefix(raw: string): string {
  const cleaned = raw.toUpperCase().replace(/[^A-Z0-9-]/g, '');
  if (cleaned.length > 20) throw new Error(`Prefix "${raw}" is too long (max 20 characters).`);
  return cleaned;
}

/**
 * The tag every code in one run shares.
 *
 * Dated so that a coupon found in the Stripe dashboard months later can be
 * traced back to the day and the person who minted it.
 */
export function batchId(now: number, pick: Pick = securePick): string {
  return `${new Date(now).toISOString().slice(0, 10)}-${randomCode('', pick).slice(0, 4)}`;
}

/* -------------------------------------------------------------------------- */
/* Parsing                                                                    */
/* -------------------------------------------------------------------------- */

export function parseDuration(raw: string): Duration {
  if (raw === 'forever') return { kind: 'forever' };
  if (raw === 'once') return { kind: 'once' };
  const months = Number(raw);
  if (!Number.isInteger(months) || months < 1 || months > 12) {
    throw new Error(`--duration must be "forever", "once", or a whole number of months 1–12 (got "${raw}").`);
  }
  return { kind: 'repeating', months };
}

/** End of the given day, so `2026-12-31` includes the 31st. Unix seconds. */
export function parseExpiry(raw: string): number {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) throw new Error(`--expires must be YYYY-MM-DD (got "${raw}").`);
  const at = new Date(`${raw}T23:59:59Z`).getTime();
  if (Number.isNaN(at)) throw new Error(`--expires is not a real date: "${raw}".`);
  return Math.floor(at / 1000);
}

/**
 * Reads an offer out of an untrusted JSON body.
 *
 * The caller of the Edge Function is an administrator, and this still validates
 * everything. Being signed in as an admin is not the same as being the admin —
 * a borrowed laptop or a stolen session is exactly the case where the number
 * `count: 5000` arrives — and the cost of a bad offer here is measured in
 * subscriptions given away rather than in a stack trace.
 *
 * Returns a message rather than throwing, because every one of these is a
 * sentence the admin page shows next to the field that caused it.
 */
export function parseOffer(
  input: unknown,
  options: { maxCount?: number } = {},
): { ok: true; offer: Offer } | { ok: false; error: string } {
  const maxCount = options.maxCount ?? MAX_CODES_PER_REQUEST;
  if (typeof input !== 'object' || input === null) return { ok: false, error: 'Malformed request.' };
  const body = input as Record<string, unknown>;

  const count = body.count ?? DEFAULT_OFFER.count;
  if (!Number.isInteger(count) || (count as number) < 1 || (count as number) > maxCount) {
    return { ok: false, error: `Ask for between 1 and ${maxCount} codes.` };
  }

  const percent = body.percent ?? DEFAULT_OFFER.percent;
  if (typeof percent !== 'number' || !Number.isFinite(percent) || percent <= 0 || percent > 100) {
    return { ok: false, error: 'The discount must be more than 0% and at most 100%.' };
  }

  let duration: Duration;
  try {
    duration = typeof body.duration === 'string' ? parseDuration(body.duration) : DEFAULT_OFFER.duration;
  } catch (cause) {
    return { ok: false, error: (cause as Error).message };
  }

  const rawTiers = body.tiers ?? [];
  if (!Array.isArray(rawTiers)) return { ok: false, error: 'Plans must be a list.' };
  const tiers: Tier[] = [];
  for (const tier of rawTiers) {
    if (tier !== 'premium' && tier !== 'family') {
      return { ok: false, error: `"${String(tier)}" is not a plan a code can be scoped to.` };
    }
    if (!tiers.includes(tier)) tiers.push(tier);
  }

  const maxRedemptions = body.maxRedemptions ?? DEFAULT_OFFER.maxRedemptions;
  if (!Number.isInteger(maxRedemptions) || (maxRedemptions as number) < 1 || (maxRedemptions as number) > 1000) {
    return { ok: false, error: 'Each code must allow between 1 and 1000 redemptions.' };
  }

  let expiresAt: number | null = null;
  if (typeof body.expires === 'string' && body.expires !== '') {
    try {
      expiresAt = parseExpiry(body.expires);
    } catch {
      return { ok: false, error: 'The expiry date must be a real date, as YYYY-MM-DD.' };
    }
  }

  let prefix: string;
  try {
    prefix = typeof body.prefix === 'string' ? normalisePrefix(body.prefix) : DEFAULT_OFFER.prefix;
  } catch (cause) {
    return { ok: false, error: (cause as Error).message };
  }

  const note = typeof body.note === 'string' ? body.note.trim() : '';
  if (note.length > MAX_NOTE_LENGTH) {
    return { ok: false, error: `The note must be ${MAX_NOTE_LENGTH} characters or fewer.` };
  }

  return {
    ok: true,
    offer: {
      count: count as number,
      percent,
      duration,
      tiers,
      maxRedemptions: maxRedemptions as number,
      expiresAt,
      prefix,
      note,
      firstTimeOnly: body.firstTimeOnly === true,
    },
  };
}

/* -------------------------------------------------------------------------- */
/* Describing                                                                 */
/* -------------------------------------------------------------------------- */

/** One human sentence, for the console, the admin page and the coupon's name. */
export function describeOffer(offer: Offer): string {
  const scope = offer.tiers.length === 0 ? 'any plan' : `${offer.tiers.join(' and ')} only`;
  const span =
    offer.duration.kind === 'forever'
      ? 'for as long as the subscription lasts'
      : offer.duration.kind === 'once'
        ? 'for the first billing period'
        : `for ${offer.duration.months} months`;
  return `${offer.percent}% off ${scope}, ${span}`;
}

/**
 * True when redeeming this code still leaves a bill to pay later.
 *
 * Worth saying out loud wherever a code is minted. Checkout uses
 * `payment_method_collection: 'if_required'`, so a forever code asks for no
 * card at all — but a code that stops covering the subscription does ask for
 * one, which is Stripe being honest and not what "free" usually means to the
 * person holding the code.
 */
export function stillNeedsACard(offer: Offer): boolean {
  return offer.percent < 100 || offer.duration.kind !== 'forever';
}

/* -------------------------------------------------------------------------- */
/* Stripe request bodies                                                      */
/* -------------------------------------------------------------------------- */

export function couponForm(offer: Offer, batch: string, productIds: readonly string[]): URLSearchParams {
  const form = new URLSearchParams({
    percent_off: String(offer.percent),
    duration: offer.duration.kind,
    name: `${describeOffer(offer)} — batch ${batch}`,
    'metadata[batch]': batch,
    'metadata[source]': '@yuan-math/promo',
  });

  if (offer.duration.kind === 'repeating') {
    form.set('duration_in_months', String(offer.duration.months));
  }
  if (offer.note) form.set('metadata[note]', offer.note);
  // `redeem_by` stops the coupon being attached after the campaign ends — the
  // backstop if a promotion code is ever created against it without an expiry.
  if (offer.expiresAt !== null) form.set('redeem_by', String(offer.expiresAt));

  productIds.forEach((id, index) => form.set(`applies_to[products][${index}]`, id));

  return form;
}

export function promotionCodeForm(
  code: string,
  couponId: string,
  offer: Offer,
  batch: string,
): URLSearchParams {
  const form = new URLSearchParams({
    coupon: couponId,
    code,
    max_redemptions: String(offer.maxRedemptions),
    'metadata[batch]': batch,
  });
  if (offer.expiresAt !== null) form.set('expires_at', String(offer.expiresAt));
  if (offer.firstTimeOnly) form.set('restrictions[first_time_transaction]', 'true');
  if (offer.note) form.set('metadata[note]', offer.note);
  return form;
}

export function toCsv(codes: readonly string[], offer: Offer, batch: string): string {
  const expires =
    offer.expiresAt === null ? '' : new Date(offer.expiresAt * 1000).toISOString().slice(0, 10);
  const rows = codes.map((code) =>
    [code, describeOffer(offer), String(offer.maxRedemptions), expires, batch].map(csvCell).join(','),
  );
  return ['code,offer,max_redemptions,expires,batch', ...rows].join('\n') + '\n';
}

const csvCell = (value: string): string =>
  /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
